import React, { useState, useLayoutEffect, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import PageTitle from '@/components/common/PageTitle';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Messages } from '@/common/constants';
import Loading from '@/components/common/Loading';
import CommonActionButton from '@/components/common/CommonActionButton';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import EmployeeMyGroupService from '@/services/EmployeeMyGroup.service';
import Employee from '@/types/Employee.type';
import { EmployeeMyGroups } from '@/types/EmployeeMyGroup.type';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CustomTextField from '@/components/common/CustomTextField';
import EmployeeService from '@/services/Employee.service';
import MyGroupDrawer from '@/components/myGroups/MyGroupDrawer';
import { MyGroup } from '@/types/MyGroup.type';
import MyGroupService from '@/services/MyGroup.service';

type DelType = {
  id: number;
  name: string;
};

type MyGroupInfo = {
  name: string;
  category: number;
};

type FormValues = {
  employeeMyGroups: {
    employeeMyGroupId: number;
    employeeId: number;
    name: string;
    sortNo: number | null | undefined | '';
    isAuthority: number;
  }[];
};

type Responses = [{ data: Employee[] }, { data: MyGroup & EmployeeMyGroups }];

const EmployeeMyGroupDetail: React.FC = () => {
  const pathParams = useParams<{ id: string }>();
  const useFormMethods = useForm<FormValues>({ shouldUnregister: false });
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(true);
  const [reloading, setReloading] = useState<boolean>(false);
  const [delEmployeeMyGroup, setDelEmployeeMyGroup] = useState<DelType>({ id: 0, name: '' });
  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [employee, setEmployee] = useState<Employee[] | []>([]);
  const [myGroup, setMyGroup] = useState<MyGroupInfo>({ name: '', category: 0 });
  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
  } = useFormMethods;
  const { fields, append, remove } = useFieldArray({
    name: 'employeeMyGroups',
    control,
  });

  useLayoutEffect(() => {
    Promise.all([EmployeeService.getExclusionMyGroup(pathParams.id!), MyGroupService.get(pathParams.id)])
      .then((responses: Responses) => {
        const [employee, employeeMyGroups] = responses;
        setEmployee(employee.data);
        remove();
        employeeMyGroups.data.employeeMyGroups.forEach((value) => {
          append({
            employeeMyGroupId: value.id,
            employeeId: value.employee.id,
            name: value.employee.name,
            sortNo: value.sortNo ?? '',
            isAuthority: value.isAuthority,
          });
        });
        setMyGroup({
          name: employeeMyGroups.data.name,
          category: employeeMyGroups.data.category,
        });
        setLoading(false);
      })
      .catch(() => {
        // エラー処理
        setLoading(false);
        enqueueSnackbar(Messages.select.failure);
      });
  }, []);

  useEffect(() => {
    if (!reloading) {
      return;
    }
    setLoading(true);
    Promise.all([EmployeeService.getExclusionMyGroup(pathParams.id!), MyGroupService.get(pathParams.id)])
      .then((responses: Responses) => {
        const [employee, employeeMyGroups] = responses;
        setEmployee(employee.data);
        remove();
        employeeMyGroups.data.employeeMyGroups.forEach((value) => {
          append({
            employeeMyGroupId: value.id,
            employeeId: value.employee.id,
            name: value.employee.name,
            sortNo: value.sortNo ?? '',
            isAuthority: value.isAuthority,
          });
        });
        setMyGroup({
          name: employeeMyGroups.data.name,
          category: employeeMyGroups.data.category,
        });
        setReloading(false);
        setLoading(false);
      })
      .catch(() => {
        // エラー処理
        setReloading(false);
        setLoading(false);
        enqueueSnackbar(Messages.select.failure);
      });
  }, [reloading]);

  const onSubmit = async (values: FormValues) => {
    try {
      await EmployeeMyGroupService.update(values, pathParams.id);
      enqueueSnackbar(Messages.update.success, { variant: 'info' });
      setReloading(true);
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };

  const deleteEmployeeMyGroup = async (id: number) => {
    try {
      await EmployeeMyGroupService.delete(id);
      setReloading(true);
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
    enqueueSnackbar(Messages.delete.success, { variant: 'info' });
  };

  const handleDialog = (id: number, name: string) => {
    setDelEmployeeMyGroup({
      id: id,
      name: name,
    });
    setOpen(true);
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={8} lg={8}>
        <Box sx= {{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>Myグループ ー メンバー編集</PageTitle>
        </Box>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 2, md: 3 } }}>
          <Box sx={{ marginBottom: '4rem' }}>
            <Box sx={{ display: 'flex' }}>
              <Box
                style={{
                  fontSize: 'x-large',
                  marginLeft: '10px',
                  marginTop: '10px',
                }}
              >
                {myGroup.name} | メンバー
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setDrawerOpen(true)}
                size="small"
                sx={{
                  marginLeft: 'auto',
                  marginTop: '10px',
                  marginRight: '40px',
                  display: pathParams.id ?? 'none',
                }}
              >
                メンバー追加
              </Button>
            </Box>
          </Box>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" width="200">
                        名前
                      </TableCell>
                      {/* <TableCell align="left" width="150">
                        権限
                        <Tooltip placement="top" arrow title="「所有者」には、グループの編集権限があります。">
                          <HelpOutlineIcon color="success" sx={{ marginLeft: 1, verticalAlign: '-6px' }} />
                        </Tooltip>
                      </TableCell> */}
                      <TableCell align="left" width="150">
                        並び順
                        <Tooltip
                          placement="top"
                          arrow
                          title="行先一覧での表示順を設定できます。※ログイン者は必ず1番上に表示されます。"
                        >
                          <HelpOutlineIcon color="success" sx={{ marginLeft: 1, verticalAlign: '-6px' }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell width="100"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((field, index) => (
                      <TableRow key={index}>
                        <TableCell>{field.name}</TableCell>
                        {/* <TableCell>
                          <Grid item key={`${index}.isAuthority`} xs={12}>
                            <CustomSelect
                              type="select"
                              name={`employeeMyGroups.${index}.isAuthority`}
                              label=""
                              disabled={myGroup.category === 1 ? true : false}
                              options={{ メンバー: 0, 所有者: 1 }}
                              rule={{ required: true }}
                            />
                          </Grid>
                        </TableCell> */}
                        <TableCell>
                          <Grid item key={`${index}.sortNo`} xs={12}>
                            <CustomTextField
                              type="number"
                              name={`employeeMyGroups.${index}.sortNo`}
                              label=""
                              inputProps={{ min: 1 }}
                              rule={{
                                max: { value: 9999, message: '表示順は9,999字以内で入力してください。' },
                                min: { value: 1, message: '表示順は1以上で入力してください。' },
                              }}
                            />
                          </Grid>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            sx={{
                              display: field.isAuthority === 1 ? 'none' : 'block',
                            }}
                            onClick={() => handleDialog(field.employeeMyGroupId, field.name)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid item xs={12} marginTop="2rem">
                <CommonActionButton isSubmitting={isSubmitting} />
              </Grid>
              <ConfirmDialog
                open={open}
                onClose={() => setOpen(false)}
                onOk={() => {
                  setOpen(false);
                  deleteEmployeeMyGroup(delEmployeeMyGroup.id);
                }}
              >
                {delEmployeeMyGroup.name}を本当に削除しますか？
              </ConfirmDialog>
            </form>
          </FormProvider>
        </Paper>
        <MyGroupDrawer
          myGroupId={pathParams.id!}
          employee={employee}
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setReloading(true);
          }}
        ></MyGroupDrawer>
      </Grid>
    </Grid>
  );
};
export default EmployeeMyGroupDetail;
